import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import xor from 'lodash/xor';
import Header from '../../components/Header';
import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import Toast from '../../components/Toast';
import Label from '../../components/Label';
import Segment from '../../components/Segment';
import List from '../../components/List';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt';

import styles from './styles.css';

const messages = defineMessages({
  diversitySettings: {
    id: 'amnext.diversity.settings',
    defaultMessage: 'Diversity Settings',
  },
  diversitySettingsMessage: {
    id: 'amnext.diversity.settings.message',
    defaultMessage: 'Select the student racial/ethnic minority group(s) that you would like to see messages and/or be asked to connect with your institution. This setting will only be applied to the locations in which Racial/Ethnic Minorities was selected as part of the audience.', // eslint-disable-line
  },
  validationError: {
    id: 'amnext.diversity.settings.message.error',
    defaultMessage: 'Please select at least one value',
  },
  successMessage: {
    id: 'amnext.diversity.settings.message.success',
    defaultMessage: 'Diversity settings have been applied successfully',
  },
  notAppliedMessage: {
    id: 'amnext.diversity.settings.message.notapplied',
    defaultMessage: 'There was a problem updating your diversity settings, please try again',
  },
  save: {
    id: 'amnext.diversity.settings.save',
    defaultMessage: 'Save',
  },
});

const diversityOptions = defineMessages({
  AMERICAN_INDIAN: {
    id: 'amnext.diversity.settings.american-indian',
    defaultMessage: 'American Indian or Alaskan Native',
  },
  ASIAN: {
    id: 'amnext.diversity.settings.asian',
    defaultMessage: 'Asian',
  },
  BLACK: {
    id: 'amnext.diversity.settings.black',
    defaultMessage: 'Black or African American',
  },
  FILIPINO: {
    id: 'amnext.diversity.settings.filipino',
    defaultMessage: 'Filipino',
  },
  HISPANIC: {
    id: 'amnext.diversity.settings.hispanic',
    defaultMessage: 'Hispanic/Latino of any race',
  },
  PACIFIC_ISLANDER: {
    id: 'amnext.diversity.settings.pacific-islander',
    defaultMessage: 'Native Hawaiian or Other Pacific Islander',
  },
  MULTI_RACIAL: {
    id: 'amnext.diversity.settings.multiracial',
    defaultMessage: 'Multiracial',
  },
  OTHER: {
    id: 'amnext.diversity.settings.other',
    defaultMessage: 'All Other Racial/Ethnic Minority Groups',
  },
});

function stateFromProps({ version, ethnicDiversities }) {
  return {
    // default to all selected if empty
    values: ethnicDiversities && ethnicDiversities.length > 0 ?
      ethnicDiversities :
      Object.keys(diversityOptions),
    pristine: (ethnicDiversities && ethnicDiversities.length === 0),
    error: false,
    success: false,
    errorMessage: '',
    version,
  };
}

class AMNextDiversitySettings extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    version: PropTypes.number,
    ethnicDiversities: PropTypes.array,
    updateAMEthnicDiversities: PropTypes.func,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState.version !== nextProps.version) {
      return stateFromProps(nextProps);
    }
    return null;
  }

  state = {
    values: [],
    error: false,
    success: false,
    errorMessage: '',
  };

  saveButtonRef = React.createRef();

  onChange = (e, { value } = {}) => {
    const { values: curValues } = this.state;
    value = value || e.currentTarget.value;
    const values = curValues.includes(value) ? curValues.filter((v) => v !== value) : curValues.concat(value);
    this.setState({ values, pristine: false });
  };

  onSave = () => {
    this.save(true);
  };

  onClose = (abort) => /* istanbul ignore next */{
    if (abort) {
      // set focus when route transition was aborted - this clears the highlight on the clicked nav menu item
      this.saveButtonRef.current.focus();
    }
  };

  handleToastDismiss = () => {
    this.setState({ success: false, errorMessage: '' });
  };

  save = async (quiet) => {
    const { version } = this.props;
    const { values: ethnicDiversities } = this.state;
    const isValid = ethnicDiversities.length > 0;
    this.setState({ error: !isValid, success: false, errorMessage: '' });
    if (isValid) {
      try {
        await this.props.updateAMEthnicDiversities({
          variables: {
            input: {
              version,
              ethnicDiversities,
            },
          },
        });
        this.setState({ success: true });
      } catch (err) {
        /* istanbul ignore else */
        if (err.message) {
          this.setState({ errorMessage: err.message });
        }
        /* istanbul ignore next */
        if (!quiet) {
          throw err;
        }
      }
    } else if (!quiet) {
      return Promise.reject(new Error('At least one ethnicity needs to be selected.'));
    }
    return Promise.resolve();
  };

  discardChanges = () => new Promise((resolve) => {
    this.setState(stateFromProps(this.props), resolve);
  });

  isDirty() {
    const { values: currentValues } = this.state;
    const { ethnicDiversities: originalValues } = this.props;
    return xor(currentValues, originalValues).length !== 0;
  }

  render() {
    const { pristine, values, error, success, errorMessage } = this.state;
    const { intl } = this.props;
    const isDirty = this.isDirty();
    return (
      <React.Fragment>
        <Segment>
          <Header as="h2" size="medium">
            { intl.formatMessage(messages.diversitySettings) }
          </Header>
          <p>
            <FormattedMessage {...messages.diversitySettingsMessage} />
          </p>
          <List className={styles.list}>
            {
              Object.keys(diversityOptions).map((key) => (
                <List.Item
                  className={styles.item}
                >
                  <Checkbox
                    onChange={this.onChange}
                    checked={values.includes(key)}
                    key={key}
                    id={`diversity-option-${key}`}
                    label={{
                      htmlFor: `diversity-option-${key}`,
                      children: intl.formatMessage(diversityOptions[key]),
                    }}
                    value={key}
                  />
                </List.Item>
              ))
            }
          </List>
          {error && (
          <Label role="tooltip" basic color="red">
            <FormattedMessage {...messages.validationError} />
          </Label>
          )}
          <div styleName="form-footer">
            <Button ref={this.saveButtonRef} onClick={this.onSave} primary>
              <FormattedMessage {...messages.save} />
            </Button>
          </div>
        </Segment>

        <UnsavedChangesPrompt
          when={isDirty && !pristine}
          saveChanges={this.save}
          discardChanges={this.discardChanges}
          onClose={this.onClose}
          closeable
        />
        <Toast
          active={success}
          title=""
          kind="success"
          onDeactivate={this.handleToastDismiss}
          description={intl.formatMessage(messages.successMessage)}
        />
        <Toast
          active={!!errorMessage}
          title=""
          kind="error"
          onDeactivate={this.handleToastDismiss}
          description={intl.formatMessage(messages.notAppliedMessage)}
        />
      </React.Fragment>
    );
  }
}

export default injectIntl(AMNextDiversitySettings);
